/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo } from 'react';
import { composeWithDevTools } from '@redux-devtools/extension';
import { legacy_createStore as createStore, applyMiddleware, combineReducers, Store, Dispatch } from 'redux';
import createSagaMiddleware from 'redux-saga';
import { all, fork } from 'redux-saga/effects';
import { setDispatch } from '@lib/error';
import * as auth from './auth';
import * as global from './global';
import * as mint from './mint';
import * as modals from './modals';
import * as settings from './settings';
import * as token from './token';
import * as user from './user';

let store: Store | undefined;
const sagaMiddleware = createSagaMiddleware();

export interface IApplicationState {
  auth: auth.IAuth;
  global: global.IGlobal;
  mint: mint.IMint;
  modals: modals.IModals;
  settings: settings.ISettings;
  token: token.IToken;
  user: user.IUserCount;
}

export const initialState: IApplicationState = {
  auth: auth.initialState,
  global: global.initialState,
  mint: mint.initialState,
  modals: modals.initialState,
  settings: settings.initialState,
  token: token.initialState,
  user: user.initialState,
};

export const rootReducer = combineReducers<IApplicationState>({
  auth: auth.reducer,
  global: global.reducer,
  mint: mint.reducer,
  modals: modals.reducer,
  settings: settings.reducer,
  token: token.reducer,
  user: user.reducer,
});

function* rootSaga() {
  yield all([fork(auth.saga), fork(global.saga), fork(settings.saga), fork(user.saga), fork(token.saga)]);
}

function initStore(preloadedState = initialState) {
  const middlewares = [sagaMiddleware];
  return createStore(rootReducer, preloadedState, composeWithDevTools(applyMiddleware(...middlewares)));
}

export const initializeStore = (preloadedState: IApplicationState) => {
  let scopedStore = store ?? initStore(preloadedState);

  // After navigating to a page with an initial Redux state, merge that state
  // with the current state in the store, and create a new store
  if (preloadedState && store) {
    scopedStore = initStore({
      ...store.getState(),
      ...preloadedState,
    });
    // Reset the current store
    store = undefined;
  }

  // add sagas
  sagaMiddleware.run(rootSaga);

  // For SSG and SSR always create a new store
  if (typeof window === 'undefined') return scopedStore;
  // Create the store once in the client
  if (!store) store = scopedStore;

  // hack: making dispatch available on error
  setDispatch(scopedStore.dispatch);

  return scopedStore;
};

export function useStore(s: IApplicationState) {
  store = useMemo(() => initializeStore(s), [s]);

  return store;
}

export function getStore() {
  return store;
}

// export type RootState = ReturnType<typeof store.getState>;
export type RootState = ReturnType<typeof rootReducer>;
export type AppDispatch = Dispatch; // store.dispatch
